import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ProposalDto, type ProposalStatus } from '@shared';
import { apiGet, ApiError } from '../../lib/api';
import { ProposalStatusChip } from '../../components/ProposalStatusChip';

type Filter = 'awaiting' | 'all';

// Client-visible labels — friendlier than the raw status enum.
const STATUS_LABEL: Record<ProposalStatus, string> = {
  draft: 'Draft',           // never shown to client (filtered server-side)
  sent: 'Awaiting decision',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

function formatPlan(plan: string): string {
  switch (plan) {
    case '50_50': return '50 / 50';
    case '30_40_30': return '30 / 40 / 30';
    case 'milestones': return 'Milestones';
    case 'success_fee': return 'Success fee';
    default: return plan;
  }
}

export function PortalProposals() {
  const [rows, setRows] = useState<ProposalDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('awaiting');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ proposals: ProposalDto[] }>('/api/proposals')
      .then((r) => setRows(r.proposals))
      .catch((err: Error) => {
        const msg = err instanceof ApiError ? err.serverMessage : err.message;
        setError(msg ?? 'Failed to load proposals.');
      });
  }, []);

  const list = useMemo(
    () => (rows ?? []).filter((p) => filter === 'awaiting' ? p.status === 'sent' : true),
    [rows, filter],
  );

  const awaiting = (rows ?? []).filter((p) => p.status === 'sent').length;

  return (
    <>
      <div>
        <h1 className="portal-h1">Proposals</h1>
        <p className="portal-sub">
          Engagement proposals from your Omega lead. Each one is tied to a brief; accept to move
          into engagement, decline to send it back.
        </p>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        <button className={filter === 'awaiting' ? 'on' : ''} onClick={() => setFilter('awaiting')}>
          Awaiting decision {awaiting > 0 && <span className="tab-count">{awaiting}</span>}
        </button>
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>
          All
        </button>
      </div>

      {error && <div className="card" style={{ marginTop: 16, color: 'var(--red)' }}>{error}</div>}
      {rows !== null && !error && list.length === 0 && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--fg-mute)' }}>
          {filter === 'awaiting'
            ? 'No proposals awaiting your decision.'
            : 'No proposals on file yet.'}
        </div>
      )}

      <div className="proposal-grid">
        {list.map((p) => (
          <Link key={p.id} to={`/portal/proposals/${p.id}`} className="card proposal-card proposal-card-link">
            <header className="proposal-hd">
              <div>
                <div className="rid">{p.id}</div>
                <div className="proposal-ref">Brief {p.request_id}</div>
              </div>
              <ProposalStatusChip status={p.status} />
            </header>

            <div className="proposal-amount">{formatMoney(p.fee_pence, p.currency)}</div>
            <div className="proposal-meta">
              <span>Payment</span>
              <strong>{formatPlan(p.payment_plan)}</strong>
            </div>
            <div className="proposal-meta">
              <span>Valid until</span>
              <strong>{p.valid_until.slice(0, 10)}</strong>
            </div>
            {p.sent_at && (
              <div className="proposal-meta">
                <span>Sent</span>
                <strong>{p.sent_at.slice(0, 10)}</strong>
              </div>
            )}
            {p.status === 'sent' && (
              <div style={{ marginTop: 14, color: 'var(--accent)', fontSize: 13, fontWeight: 500 }}>
                Review &amp; respond →
              </div>
            )}
          </Link>
        ))}
      </div>
    </>
  );
}
